import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Notepad, PencilSimple, TrashSimple, ChartBar, ArrowRight, ShareNetwork } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { engApi, FORM_STATUS_LABEL, FORM_STATUS_BADGE } from './engApi';
import { EngFormShareDialog } from './EngFormShareDialog';

export default function EngForms() {
  const navigate = useNavigate();
  const { access } = useOutletContext() || {};
  const isMember = !!access?.is_member;
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareForm, setShareForm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    engApi.forms().then((r) => setForms(r.forms || [])).catch(() => toast.error('フォームの取得に失敗しました')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (f) => {
    if (!window.confirm(`「${f.title}」を削除しますか？回答データも削除されます。`)) return;
    try {
      await engApi.deleteForm(f.id);
      toast.success('削除しました');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  return (
    <div className="space-y-5" data-testid="eng-forms-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900">データ収集</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isMember ? '現場データ収集フォームの作成・回答の分析' : '受付中のフォームに回答できます（写真撮影対応）'}
          </p>
        </div>
        {isMember && (
          <Button onClick={() => navigate('/engineering/forms/new')} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-new-form-btn">
            <Plus size={18} weight="bold" className="mr-1" /> フォームを作成
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-400">読込中…</div>
      ) : forms.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-zinc-300 p-10 text-center" data-testid="eng-forms-empty">
          <Notepad size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">{isMember ? 'フォームがありません' : '現在回答できるフォームはありません'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="eng-forms-grid">
          {forms.map((f) => (
            <div key={f.id} className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-testid={`eng-form-card-${f.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="text-sm font-semibold text-zinc-900 leading-snug flex-1">{f.title}</h3>
                <Badge variant="outline" className={`${FORM_STATUS_BADGE[f.status]} text-[10px] shrink-0`}>{FORM_STATUS_LABEL[f.status]}</Badge>
              </div>
              {f.description && <p className="text-xs text-zinc-500 line-clamp-2 mb-2">{f.description}</p>}
              <div className="text-[11px] text-zinc-400 mb-3">
                {(f.fields || []).length}項目{isMember && <> · 回答 {f.response_count || 0}件</>}
              </div>
              <div className="mt-auto flex items-center gap-2 flex-wrap">
                {f.status === 'open' && (
                  <Button size="sm" onClick={() => navigate(`/engineering/forms/${f.id}/fill`)}
                    className="h-8 bg-teal-700 hover:bg-teal-800" data-testid={`eng-form-fill-${f.id}`}>
                    回答する <ArrowRight size={13} className="ml-1" />
                  </Button>
                )}
                {isMember && (
                  <>
                    {f.status === 'open' && (
                      <Button size="sm" variant="outline" onClick={() => setShareForm(f)}
                        className="h-8 border-teal-200 text-teal-700 hover:bg-teal-50" data-testid={`eng-form-share-${f.id}`}>
                        <ShareNetwork size={13} className="mr-1" /> 共有
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => navigate(`/engineering/forms/${f.id}/responses`)}
                      className="h-8" data-testid={`eng-form-responses-${f.id}`}>
                      <ChartBar size={13} className="mr-1" /> 回答
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/engineering/forms/${f.id}/edit`)}
                      className="h-8 w-8 p-0" data-testid={`eng-form-edit-${f.id}`}>
                      <PencilSimple size={13} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(f)}
                      className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50" data-testid={`eng-form-delete-${f.id}`}>
                      <TrashSimple size={13} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <EngFormShareDialog open={!!shareForm} onClose={() => setShareForm(null)} form={shareForm} />
    </div>
  );
}

